import React, { Fragment, useState, useEffect } from 'react';
import { Breadcrumbs } from '../../../AbstractElements';
import { Container, Row, Col, Card, CardHeader, CardBody, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import { Edit } from 'react-feather';
import { useNavigate, useParams } from 'react-router-dom';
import API_BASE_URL from '../../../Config/api';
import authHeader from '../../Customer/authHeader';
import CustomerStoresManager from './CustomerStoresManager';

const EditCustomer = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [formData, setFormData] = useState({
        store_name: '',
        extra_info: '',
        vat_eori: '',
        email: '',
        tel: '',
        shipping_address: '',
        billing_address: '',
        country: '',
        showroom: ''
    });
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchCustomer = async () => {
            try {
                const response = await fetch(`${API_BASE_URL}/api/customers/${id}`, authHeader());
                if (response.ok) {
                    const customer = await response.json();
                    setFormData({
                        store_name: customer.store_name || '',
                        extra_info: customer.extra_info || '',
                        vat_eori: customer.vat_eori || '',
                        email: customer.email || '',
                        tel: customer.tel || '',
                        shipping_address: customer.shipping_address || '',
                        billing_address: customer.billing_address || '',
                        country: customer.country || '',
                        showroom: customer.showroom || ''
                    });
                } else {
                    alert('Customer not found');
                    navigate(`${process.env.PUBLIC_URL}/all-customers`);
                }
            } catch (error) {
                console.error('Error fetching customer:', error);
                alert('Error loading customer data');
            } finally {
                setLoading(false);
            }
        };

        fetchCustomer();
    }, [id, navigate]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        if (errors[name]) {
            setErrors({
                ...errors,
                [name]: ''
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.store_name.trim()) {
            newErrors.store_name = 'Store name is required';
        }

        if (formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Invalid email format';
        }

        if (formData.tel && !/^\+?[\d\s-()]+$/.test(formData.tel)) {
            newErrors.tel = 'Invalid phone number format';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);

        try {
            const response = await fetch(`${API_BASE_URL}/api/customers/${id}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    ...authHeader().headers,
                },
                body: JSON.stringify(formData)
            });

            if (response.ok) {
                alert('Customer updated successfully!');
                navigate(`${process.env.PUBLIC_URL}/all-customers`);
            } else {
                const errorData = await response.json();
                alert(`Failed to update customer: ${errorData.message || 'Unknown error'}`);
            }
        } catch (error) {
            console.error('Error updating customer:', error);
            alert('Error updating customer. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancel = () => {
        navigate(`${process.env.PUBLIC_URL}/all-customers`);
    };

    if (loading) {
        return (
            <Fragment>
                <Breadcrumbs parent="Customers" title="Edit Customer" mainTitle="Edit Customer" />
                <Container fluid={true}>
                    <Row>
                        <Col sm="12">
                            <Card>
                                <CardBody>
                                    <div className="text-center">Loading...</div>
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </Fragment>
        );
    }

    return (
        <Fragment>
            <Breadcrumbs parent="Customers" title="Edit Customer" mainTitle="Edit Customer" />
            <Container fluid={true}>
                <Row>
                    <Col sm="12">
                        <Card>
                            <CardHeader>
                                <h5>
                                    <Edit className="me-2" size={20} />
                                    Edit Customer
                                </h5>
                            </CardHeader>
                            <CardBody>
                                <Form onSubmit={handleSubmit}>
                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="store_name">Store Name <span className="text-danger">*</span></Label>
                                                <Input
                                                    type="text"
                                                    name="store_name"
                                                    id="store_name"
                                                    value={formData.store_name}
                                                    onChange={handleChange}
                                                    invalid={!!errors.store_name}
                                                    placeholder="Enter store name"
                                                />
                                                {errors.store_name && <div className="text-danger small">{errors.store_name}</div>}
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="vat_eori">VAT/EORI</Label>
                                                <Input
                                                    type="text"
                                                    name="vat_eori"
                                                    id="vat_eori"
                                                    value={formData.vat_eori}
                                                    onChange={handleChange}
                                                    placeholder="Enter VAT/EORI number"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="12">
                                            <FormGroup>
                                                <Label for="extra_info">Extra Information</Label>
                                                <Input
                                                    type="textarea"
                                                    name="extra_info"
                                                    rows="4"
                                                    value={formData.extra_info}
                                                    onChange={handleChange}
                                                    placeholder="Enter extra information"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="email">Email</Label>
                                                <Input
                                                    type="email"
                                                    name="email"
                                                    id="email"
                                                    value={formData.email}
                                                    onChange={handleChange}
                                                    invalid={!!errors.email}
                                                    placeholder="Enter email address"
                                                />
                                                {errors.email && <div className="text-danger small">{errors.email}</div>}
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="tel">Tel</Label>
                                                <Input
                                                    type="text"
                                                    name="tel"
                                                    id="tel"
                                                    value={formData.tel}
                                                    onChange={handleChange}
                                                    invalid={!!errors.tel}
                                                    placeholder="Enter phone number"
                                                />
                                                {errors.tel && <div className="text-danger small">{errors.tel}</div>}
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="shipping_address">Shipping Address</Label>
                                                <Input
                                                    type="textarea"
                                                    name="shipping_address"
                                                    rows="3"
                                                    value={formData.shipping_address}
                                                    onChange={handleChange}
                                                    placeholder="Enter shipping address"
                                                />
                                            </FormGroup>
                                        </Col>
                                        <Col md="6">
                                            <FormGroup>
                                                <Label for="billing_address">Billing Address</Label>
                                                <Input
                                                    type="textarea"
                                                    name="billing_address"
                                                    rows="3"
                                                    value={formData.billing_address}
                                                    onChange={handleChange}
                                                    placeholder="Enter billing address"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <Row>
                                        <Col md="12">
                                            <FormGroup>
                                                <Label for="showroom">Showroom</Label>
                                                <Input
                                                    type="text"
                                                    name="showroom"
                                                    id="showroom"
                                                    value={formData.showroom}
                                                    onChange={handleChange}
                                                    placeholder="Enter showroom location"
                                                />
                                            </FormGroup>
                                        </Col>
                                    </Row>

                                    <div className="mt-3">
                                        <Button color="primary" type="submit" disabled={submitting}>
                                            {submitting ? 'Updating...' : 'Update Customer'}
                                        </Button>
                                        <Button color="secondary" className="ms-2" onClick={handleCancel} type="button">
                                            Cancel
                                        </Button>
                                    </div>
                                </Form>
                            </CardBody>
                        </Card>
                        <CustomerStoresManager customerId={id} />
                    </Col>
                </Row>
            </Container>
        </Fragment>
    );
};

export default EditCustomer;
